import React, { memo, useEffect } from "react";
import { Slider, Stack } from "investira.react.components";

/**
 * Renderiza um slider de intervalo com distância mínima entre os extremos.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {string} props.label - Texto usado nos atributos de acessibilidade do slider.
 * @param {number} props.minDistance - Distância mínima mantida entre os dois valores.
 * @param {Function} props.onChange - Callback executado sempre que o intervalo muda.
 * @param {Object} props.sliderProps - Props adicionais repassadas ao slider interno.
 * @param {Array<number>} props.defaultValue - Valores iniciais mínimo e máximo do intervalo.
 * @returns {JSX.Element} Elemento renderizado pelo componente.
 */
const RangeSlider = memo((props) => {
  const {
    label = "Label",
    minDistance = 1,
    onChange,
    sliderProps,
    defaultValue = [0, 100],
  } = props;

  function valuetext(value) {
    return `${value}%`;
  }

  const [value, setValue] = React.useState(defaultValue);
  const xDefaultMin = defaultValue[0];
  const xDefaultMax = defaultValue[1];

  const handleChange = (_pEvent, pNewValue, pActiveThumb) => {
    if (!Array.isArray(pNewValue)) {
      return;
    }

    if (pActiveThumb === 0) {
      setValue([Math.min(pNewValue[0], value[1] - minDistance), value[1]]);
    } else {
      setValue([value[0], Math.max(pNewValue[1], value[0] + minDistance)]);
    }
  };

  useEffect(() => {
    setValue((pValue) => {
      if (pValue[0] === xDefaultMin && pValue[1] === xDefaultMax) {
        return pValue;
      }

      return [xDefaultMin, xDefaultMax];
    });
  }, [xDefaultMax, xDefaultMin]);

  useEffect(() => {
    onChange && onChange(value);
  }, [value]);

  return (
    <Stack sx={{ mx: 2, my: 2 }}>
      <Slider
        label={label}
        getAriaLabel={() => label}
        value={value}
        onChange={handleChange}
        valueLabelDisplay="auto"
        getAriaValueText={valuetext}
        disableSwap
        {...sliderProps}
      />
    </Stack>
  );
});

export default RangeSlider;
